const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Floating-Point Arithmetic & Error Sources ─────────
function DiagramFloatingPoint() {
  const W = 900, H = 340;
  // Three panels: IEEE float layout | rounding/cancellation | error hierarchy
  const PW = (W - 56) / 3;
  const p = i => 14 + i * (PW + 14);

  // Panel 1: IEEE 754 double bit layout (64-bit)
  // sign(1) | exponent(11) | mantissa(52)  – shown schematically
  const bitGroups = [
    { label:'S', bits:1,  color:'#c0392b', bg:'#fff0f0', desc:'Sign' },
    { label:'Exponent', bits:11, color:'#e07b00', bg:'#fff7ee', desc:'11 bits (biased)' },
    { label:'Mantissa (fraction)', bits:52, color:'#2E75B6', bg:'#EBF3FB', desc:'52 bits' },
  ];
  const totalBits = 64;
  const barX = p(0)+12, barY = 68, barW = PW-24, barH = 28;
  let bitOffset = 0;

  // Panel 2 data: cancellation example
  const a = 1.000000001, b = 1.000000000;
  const diff = a - b; // ~1e-9

  // Panel 3: error type hierarchy rows
  const errTypes = [
    { name:'Rounding error',    desc:'|fl(x)−x| / |x| <= eps/2',        color:'#2E75B6', bg:'#EBF3FB' },
    { name:'Truncation error',  desc:'From finite algorithm steps',       color:'#27ae60', bg:'#f0fff4' },
    { name:'Cancellation',      desc:'Subtracting nearly equal numbers',  color:'#c0392b', bg:'#fff0f0' },
    { name:'Propagated error',  desc:'Errors amplified through ops',      color:'#9b59b6', bg:'#f5d0ff' },
    { name:'Machine epsilon',   desc:'eps = 2^-52 ≈ 2.22e-16  (double)', color:'#e07b00', bg:'#fff7ee' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Floating-Point Arithmetic — IEEE 754, Rounding, and Error Sources</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* ── Panel 1: IEEE 754 layout ── */}
        <rect x={p(0)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(0)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">IEEE 754 Double (64-bit)</text>
        <text x={p(0)+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">fl(x) = (-1)^s * 2^(e-1023) * (1.fraction)</text>
        <text x={p(0)+PW/2} y={58} textAnchor="middle" fontSize="10" fill="#888">64 total bits</text>

        {/* Bit bar */}
        {bitGroups.map(({ label, bits, color, bg }) => {
          const w = (bits / totalBits) * barW;
          const x = barX + (bitOffset / totalBits) * barW;
          const rendered = (
            <g key={label}>
              <rect x={x} y={barY} width={w} height={barH} fill={bg} stroke={color} strokeWidth="1.5"/>
              <text x={x + w/2} y={barY + barH/2 + 4} textAnchor="middle"
                fontSize={bits <= 2 ? 9 : 10} fill={color} fontWeight="700">
                {bits <= 2 ? bits+'b' : label}
              </text>
            </g>
          );
          bitOffset += bits;
          return rendered;
        })}

        {/* Bracket labels below bar */}
        {(() => {
          let off = 0;
          return bitGroups.map(({ label, bits, color, desc }) => {
            const x = barX + (off / totalBits) * barW;
            const w = (bits / totalBits) * barW;
            const cx = x + w/2;
            const el = (
              <g key={label+'lbl'}>
                <line x1={x+1} y1={barY+barH} x2={x+1} y2={barY+barH+8} stroke={color} strokeWidth="1"/>
                <line x1={x+w-1} y1={barY+barH} x2={x+w-1} y2={barY+barH+8} stroke={color} strokeWidth="1"/>
                <line x1={x+1} y1={barY+barH+8} x2={x+w-1} y2={barY+barH+8} stroke={color} strokeWidth="1"/>
                <text x={cx} y={barY+barH+20} textAnchor="middle" fontSize="10" fill={color}>{desc}</text>
              </g>
            );
            off += bits;
            return el;
          });
        })()}

        {/* Key facts */}
        {[
          { y:130, bg:'#EBF3FB', bc:'#2E75B6', txt:['Range: ~5e-324 to ~1.8e+308','Precision: ~15-17 decimal digits'] },
          { y:170, bg:'#fff0f0', bc:'#c0392b', txt:['Special: 0, -0, Inf, -Inf, NaN','Overflow/underflow lose all info'] },
          { y:210, bg:'#fff7ee', bc:'#e07b00', txt:['Machine eps: 2^-52 ≈ 2.22e-16','Smallest gap between 1.0 and next float'] },
          { y:250, bg:'#f0fff4', bc:'#27ae60', txt:['Relative error of rounding:','|fl(x)-x|/|x| <= eps/2 = u'] },
        ].map(({ y, bg, bc, txt }) => (
          <g key={y}>
            <rect x={p(0)+12} y={y} width={PW-24} height={34} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            {txt.map((t, i) => (
              <text key={i} x={p(0)+20} y={y+14+i*14} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{t}</text>
            ))}
          </g>
        ))}

        {/* ── Panel 2: Cancellation ── */}
        <rect x={p(1)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(1)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Catastrophic Cancellation</text>

        {/* Number line showing near-equal subtraction */}
        {(() => {
          const nlX = p(1)+20, nlY = 90, nlW = PW-40;
          const aPos = nlX + nlW*0.35;
          const bPos = nlX + nlW*0.5;
          return (
            <g>
              <line x1={nlX} y1={nlY} x2={nlX+nlW} y2={nlY} stroke="#1F3864" strokeWidth="1.5"/>
              <polygon points={`${nlX+nlW},${nlY} ${nlX+nlW-7},${nlY-3} ${nlX+nlW-7},${nlY+3}`} fill="#1F3864"/>
              {/* a and b marks */}
              <line x1={aPos} y1={nlY-8} x2={aPos} y2={nlY+8} stroke="#2E75B6" strokeWidth="2"/>
              <text x={aPos} y={nlY-14} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">b</text>
              <line x1={bPos} y1={nlY-8} x2={bPos} y2={nlY+8} stroke="#c0392b" strokeWidth="2"/>
              <text x={bPos} y={nlY-14} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">a</text>
              {/* Brace showing tiny difference */}
              <line x1={aPos} y1={nlY+12} x2={bPos} y2={nlY+12} stroke="#9b59b6" strokeWidth="1.5"/>
              <line x1={aPos} y1={nlY+9} x2={aPos} y2={nlY+15} stroke="#9b59b6" strokeWidth="1.5"/>
              <line x1={bPos} y1={nlY+9} x2={bPos} y2={nlY+15} stroke="#9b59b6" strokeWidth="1.5"/>
              <text x={(aPos+bPos)/2} y={nlY+26} textAnchor="middle" fontSize="10" fill="#9b59b6" fontWeight="700">a - b tiny</text>
            </g>
          );
        })()}

        {/* Digit loss illustration */}
        {[
          { y:125, label:'  a = 1.23456789012345', color:'#c0392b' },
          { y:141, label:'- b = 1.23456789012344', color:'#2E75B6' },
          { y:157, label:'-------------------------------', color:'#888' },
          { y:173, label:'  result = 0.00000000000001', color:'#9b59b6' },
          { y:189, label:'  = 1e-14  (1 sig. digit!)', color:'#9b59b6' },
        ].map(({ y, label, color }) => (
          <text key={y} x={p(1)+16} y={y} fontSize="10.5" fill={color} fontFamily="'Courier New',monospace">{label}</text>
        ))}

        {/* Digits lost visual */}
        <rect x={p(1)+12} y={202} width={PW-24} height={28} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={p(1)+20} y={215} fontSize="10" fill="#1F3864" fontWeight="700">Significant digits LOST:</text>
        <text x={p(1)+20} y={228} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">14 digits cancelled, 1 left</text>

        {/* Remedies */}
        <rect x={p(1)+12} y={240} width={PW-24} height={76} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={p(1)+20} y={256} fontSize="11" fill="#27ae60" fontWeight="700">Avoiding cancellation:</text>
        <text x={p(1)+20} y={272} fontSize="10" fill="#333">- Reformulate: sqrt(x+h)-sqrt(x)</text>
        <text x={p(1)+20} y={288} fontSize="10" fill="#333">  = h / (sqrt(x+h)+sqrt(x))</text>
        <text x={p(1)+20} y={304} fontSize="10" fill="#333">- Compensated summation (Kahan)</text>
        <text x={p(1)+20} y={308} fontSize="10" fill="#555"/>

        {/* ── Panel 3: Error types ── */}
        <rect x={p(2)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={p(2)+PW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Error Type Hierarchy</text>

        {errTypes.map(({ name, desc, color, bg }, i) => {
          const y = 38 + i * 54;
          return (
            <g key={i}>
              <rect x={p(2)+12} y={y} width={PW-24} height={46} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
              <text x={p(2)+20} y={y+16} fontSize="11" fill={color} fontWeight="700">{name}</text>
              <text x={p(2)+20} y={y+32} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{desc}</text>
            </g>
          );
        })}

        {/* Condition number preview */}
        <rect x={p(2)+12} y={38+5*54} width={PW-24} height={46} rx={3} fill="#e8eaf6" stroke="#1F3864" strokeWidth="1"/>
        <text x={p(2)+20} y={38+5*54+16} fontSize="11" fill="#1F3864" fontWeight="700">Condition number kappa(A)</text>
        <text x={p(2)+20} y={38+5*54+32} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">kappa = ||A|| * ||A^-1||</text>

      </svg>
      <div style={S.dCaption}>Every real number is rounded to the nearest representable float, introducing relative error at most eps/2 (unit roundoff u = 2^-52 for double precision). Catastrophic cancellation occurs when nearly equal numbers are subtracted, destroying significant digits. The condition number quantifies how much these errors are amplified by a matrix computation.</div>
    </div>
  );
}

// ── Diagram 2: LU Factorisation & Gaussian Elimination ───────────
function DiagramLU() {
  const W = 900, H = 380;
  const LP = 14, LW = 430;
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Left: step-by-step elimination on a 3x3 matrix
  // A = [[2,1,1],[4,3,3],[8,7,9]]
  // After pivot step 1: zero out col 1 below row 1
  // After pivot step 2: zero out col 2 below row 2

  const cellW = 48, cellH = 32;

  function MatGrid({ x, y, data, highlights, label, labelColor }) {
    const n = data.length;
    return (
      <g>
        <text x={x + (n * cellW)/2} y={y - 6} textAnchor="middle" fontSize="11" fill={labelColor||'#1F3864'} fontWeight="700">{label}</text>
        {data.map((row, r) =>
          row.map((val, c) => {
            const hk = `${r}-${c}`;
            const hl = highlights && highlights[hk];
            return (
              <g key={hk}>
                <rect x={x + c*cellW} y={y + r*cellH} width={cellW-2} height={cellH-2}
                  rx={2}
                  fill={hl ? hl.bg : '#fafafa'}
                  stroke={hl ? hl.bc : '#ddd'}
                  strokeWidth={hl ? 1.5 : 0.8}/>
                <text x={x + c*cellW + (cellW-2)/2} y={y + r*cellH + (cellH-2)/2 + 4}
                  textAnchor="middle" fontSize="11"
                  fill={hl ? hl.color : '#555'}
                  fontFamily="'Courier New',monospace"
                  fontWeight={hl ? '700' : '400'}>
                  {val}
                </text>
              </g>
            );
          })
        )}
        {/* Brackets */}
        <line x1={x-4} y1={y-2}             x2={x-4}            y2={y+n*cellH-2} stroke="#888" strokeWidth="1.5"/>
        <line x1={x-4} y1={y-2}             x2={x+4}            y2={y-2}         stroke="#888" strokeWidth="1.5"/>
        <line x1={x-4} y1={y+n*cellH-2}    x2={x+4}            y2={y+n*cellH-2} stroke="#888" strokeWidth="1.5"/>
        <line x1={x+n*cellW-2+4} y1={y-2}  x2={x+n*cellW-2+4} y2={y+n*cellH-2} stroke="#888" strokeWidth="1.5"/>
        <line x1={x+n*cellW-2+4} y1={y-2}  x2={x+n*cellW-6}   y2={y-2}         stroke="#888" strokeWidth="1.5"/>
        <line x1={x+n*cellW-2+4} y1={y+n*cellH-2} x2={x+n*cellW-6} y2={y+n*cellH-2} stroke="#888" strokeWidth="1.5"/>
      </g>
    );
  }

  const A0 = [['2','1','1'],['4','3','3'],['8','7','9']];
  // After step 1: multiply m21=2, m31=4; subtract
  const A1 = [['2','1','1'],['0','1','1'],['0','3','5']];
  // After step 2: multiply m32=3; subtract
  const A2 = [['2','1','1'],['0','1','1'],['0','0','2']];
  // L (lower) and U (upper)
  const Lmat = [['1','0','0'],['2','1','0'],['4','3','1']];
  const Umat = [['2','1','1'],['0','1','1'],['0','0','2']];

  const hl1 = { '1-0':{bg:'#fff0f0',bc:'#c0392b',color:'#c0392b'}, '2-0':{bg:'#fff0f0',bc:'#c0392b',color:'#c0392b'} };
  const hl2 = { '2-1':{bg:'#fff7ee',bc:'#e07b00',color:'#e07b00'} };
  const hlL = {
    '1-0':{bg:'#EBF3FB',bc:'#2E75B6',color:'#2E75B6'},
    '2-0':{bg:'#EBF3FB',bc:'#2E75B6',color:'#2E75B6'},
    '2-1':{bg:'#EBF3FB',bc:'#2E75B6',color:'#2E75B6'},
  };
  const hlU = {
    '0-0':{bg:'#f5d0ff',bc:'#9b59b6',color:'#9b59b6'},
    '1-1':{bg:'#f5d0ff',bc:'#9b59b6',color:'#9b59b6'},
    '2-2':{bg:'#f5d0ff',bc:'#9b59b6',color:'#9b59b6'},
  };

  const rowY = 40;
  const step1X = LP + 14;
  const step2X = step1X + 3*cellW + 50;
  const step3X = step2X + 3*cellW + 50;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>LU Factorisation — Gaussian Elimination Steps and the PA=LU Decomposition</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Gaussian Elimination Steps</text>

        {/* Step labels */}
        <text x={step1X + 3*cellW/2} y={rowY-8} textAnchor="middle" fontSize="10" fill="#888">Original A</text>
        <text x={step2X + 3*cellW/2} y={rowY-8} textAnchor="middle" fontSize="10" fill="#c0392b">Step 1: zero col 1</text>
        <text x={step3X + 3*cellW/2} y={rowY-8} textAnchor="middle" fontSize="10" fill="#e07b00">Step 2: zero col 2</text>

        <MatGrid x={step1X} y={rowY} data={A0} highlights={{}} label="" />
        {/* Arrow */}
        <line x1={step1X+3*cellW+8} y1={rowY+3*cellH/2-5} x2={step2X-10} y2={rowY+3*cellH/2-5} stroke="#888" strokeWidth="1.5"/>
        <polygon points={`${step2X-10},${rowY+3*cellH/2-5} ${step2X-18},${rowY+3*cellH/2-9} ${step2X-18},${rowY+3*cellH/2-1}`} fill="#888"/>
        <MatGrid x={step2X} y={rowY} data={A1} highlights={hl1} label="" />
        {/* Arrow */}
        <line x1={step2X+3*cellW+8} y1={rowY+3*cellH/2-5} x2={step3X-10} y2={rowY+3*cellH/2-5} stroke="#888" strokeWidth="1.5"/>
        <polygon points={`${step3X-10},${rowY+3*cellH/2-5} ${step3X-18},${rowY+3*cellH/2-9} ${step3X-18},${rowY+3*cellH/2-1}`} fill="#888"/>
        <MatGrid x={step3X} y={rowY} data={A2} highlights={hl2} label="" />

        {/* Multipliers text */}
        <text x={step1X + 3*cellW + 22} y={rowY + 3*cellH/2 - 16} textAnchor="middle" fontSize="9" fill="#c0392b">m21=2</text>
        <text x={step1X + 3*cellW + 22} y={rowY + 3*cellH/2 + 2} textAnchor="middle" fontSize="9" fill="#c0392b">m31=4</text>
        <text x={step2X + 3*cellW + 22} y={rowY + 3*cellH/2 - 8} textAnchor="middle" fontSize="9" fill="#e07b00">m32=3</text>

        {/* L and U */}
        <text x={LP + LW/2} y={rowY + 3*cellH + 28} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">A = L · U  (multipliers stored in L, upper triangle is U)</text>

        <MatGrid x={step1X} y={rowY + 3*cellH + 42} data={Lmat} highlights={hlL} label="L (lower)" labelColor="#2E75B6"/>
        {/* × sign */}
        <text x={step1X + 3*cellW + 20} y={rowY + 3*cellH + 42 + 3*cellH/2} textAnchor="middle" fontSize="18" fill="#888">·</text>
        <MatGrid x={step2X} y={rowY + 3*cellH + 42} data={Umat} highlights={hlU} label="U (upper)" labelColor="#9b59b6"/>
        {/* = sign */}
        <text x={step2X + 3*cellW + 20} y={rowY + 3*cellH + 42 + 3*cellH/2} textAnchor="middle" fontSize="18" fill="#888">=</text>
        <MatGrid x={step3X} y={rowY + 3*cellH + 42} data={A0} highlights={{}} label="A (original)" labelColor="#1F3864"/>

        {/* Right panel */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Solving Ax = b with LU</text>

        {[
          { y:36,  bg:'#EBF3FB', bc:'#2E75B6', color:'#2E75B6',
            step:'1. Factorise: PA = LU', note:'O(n³/3) ops. Pivot P for stability.' },
          { y:88,  bg:'#f0fff4', bc:'#27ae60', color:'#27ae60',
            step:'2. Forward sub: Ly = Pb', note:'O(n²) ops. L is unit lower triangular.' },
          { y:140, bg:'#fff7ee', bc:'#e07b00', color:'#e07b00',
            step:'3. Back sub: Ux = y', note:'O(n²) ops. U is upper triangular.' },
          { y:192, bg:'#f5d0ff', bc:'#9b59b6', color:'#9b59b6',
            step:'Multiple RHS: same LU', note:'Factorise once; solve many b at O(n²).' },
        ].map(({ y, bg, bc, color, step, note }) => (
          <g key={y}>
            <rect x={RP+10} y={y} width={RW-20} height={44} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+18} y={y+16} fontSize="12" fill={color} fontWeight="700">{step}</text>
            <text x={RP+18} y={y+33} fontSize="11" fill="#444">{note}</text>
          </g>
        ))}

        {/* Pivoting box */}
        <rect x={RP+10} y={244} width={RW-20} height={60} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={RP+18} y={260} fontSize="12" fill="#c0392b" fontWeight="700">Partial Pivoting (PA = LU)</text>
        <text x={RP+18} y={276} fontSize="11" fill="#444">Swap largest-magnitude element to pivot</text>
        <text x={RP+18} y={292} fontSize="11" fill="#444">Prevents division by small numbers</text>
        <text x={RP+18} y={308} fontSize="10" fill="#555" fontFamily="'Courier New',monospace">|L entries| &lt;= 1  (growth controlled)</text>

        {/* Complexity box */}
        <rect x={RP+10} y={312} width={RW-20} height={46} rx={3} fill="#e8eaf6" stroke="#1F3864" strokeWidth="1"/>
        <text x={RP+18} y={328} fontSize="11" fill="#1F3864" fontWeight="700">Complexity summary</text>
        <text x={RP+18} y={344} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">Factorisation: O(2n³/3)   Solve: O(n²)</text>
        <text x={RP+18} y={357} fontSize="10" fill="#555">Best for dense systems; sparse: use sparse LU</text>

      </svg>
      <div style={S.dCaption}>Gaussian elimination builds the LU factorisation A = LU by storing row multipliers in L and the reduced form in U. With partial pivoting (PA = LU), the permutation P reorders rows to keep multipliers bounded, ensuring numerical stability. Once factorised, any number of right-hand sides can be solved in O(n²) with forward and back substitution.</div>
    </div>
  );
}

// ── Diagram 3: QR Factorisation ──────────────────────────────────
function DiagramQR() {
  const W = 900, H = 350;
  const PW = (W - 42) / 2;
  const pL = 14, pR = pL + PW + 14;

  // Left: Gram-Schmidt visualisation in 2D
  const ox = pL + 70, oy = 230, sc = 1;

  // Two vectors a1=(3,1), a2=(1,3)
  const a1 = [130, -44], a2 = [44, -128];
  // q1 = a1/|a1|
  const mag1 = Math.sqrt(a1[0]*a1[0]+a1[1]*a1[1]);
  const q1 = [a1[0]/mag1*90, a1[1]/mag1*90];
  // project a2 onto q1
  const proj_scale = (a2[0]*q1[0] + a2[1]*q1[1]) / (q1[0]*q1[0]+q1[1]*q1[1]);
  const proj = [proj_scale*q1[0], proj_scale*q1[1]];
  // orthogonal residual
  const res = [a2[0]-proj[0], a2[1]-proj[1]];
  const magR = Math.sqrt(res[0]*res[0]+res[1]*res[1]);
  const q2 = [res[0]/magR*90, res[1]/magR*90];

  function arr(x1,y1,x2,y2,color,sw=2.2) {
    const dx=x2-x1,dy=y2-y1,len=Math.sqrt(dx*dx+dy*dy);
    if(len<1) return null;
    const ux=dx/len,uy=dy/len;
    const hx=x2-8*ux,hy=y2-8*uy;
    const px=-uy,py=ux;
    return (
      <g>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={sw}/>
        <polygon points={`${x2},${y2} ${hx+4*px},${hy+4*py} ${hx-4*px},${hy-4*py}`} fill={color}/>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>QR Factorisation — Gram-Schmidt Orthogonalisation and Applications</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={pL} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pL+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Gram-Schmidt: Building Q from Columns</text>
        <text x={pL+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">A = [a1 | a2]  gives  Q = [q1 | q2],  R upper triangular</text>

        {/* Axes */}
        <line x1={ox-20} y1={oy} x2={ox+160} y2={oy} stroke="#ccc" strokeWidth="1"/>
        <line x1={ox} y1={oy+20} x2={ox} y2={oy-180} stroke="#ccc" strokeWidth="1"/>

        {/* a1, a2 */}
        {arr(ox,oy, ox+a1[0],oy+a1[1], '#c0392b')}
        <text x={ox+a1[0]+6} y={oy+a1[1]+4} fontSize="13" fill="#c0392b" fontWeight="700">a₁</text>
        {arr(ox,oy, ox+a2[0],oy+a2[1], '#e07b00')}
        <text x={ox+a2[0]-18} y={oy+a2[1]-6} fontSize="13" fill="#e07b00" fontWeight="700">a₂</text>

        {/* q1 */}
        {arr(ox,oy, ox+q1[0],oy+q1[1], '#2E75B6', 2.5)}
        <text x={ox+q1[0]+6} y={oy+q1[1]+4} fontSize="13" fill="#2E75B6" fontWeight="700">q₁</text>

        {/* Projection of a2 onto q1 */}
        <line x1={ox+a2[0]} y1={oy+a2[1]} x2={ox+proj[0]} y2={oy+proj[1]}
          stroke="#9b59b6" strokeWidth="1.4" strokeDasharray="5,3"/>
        <text x={ox+proj[0]/2+a2[0]/2-10} y={oy+(proj[1]+a2[1])/2-8} fontSize="10" fill="#9b59b6">proj</text>
        {arr(ox,oy, ox+proj[0],oy+proj[1], '#9b59b6', 1.5)}

        {/* q2 (orthogonal residual) */}
        {arr(ox,oy, ox+q2[0],oy+q2[1], '#27ae60', 2.5)}
        <text x={ox+q2[0]+6} y={oy+q2[1]-4} fontSize="13" fill="#27ae60" fontWeight="700">q₂</text>

        {/* Right angle marker between q1 and q2 */}
        {(() => {
          const s = 12;
          const u1=[q1[0]/90,q1[1]/90], u2=[q2[0]/90,q2[1]/90];
          const mx = ox + s*u1[0] + s*u2[0];
          const my = oy + s*u1[1] + s*u2[1];
          return (
            <g>
              <line x1={ox+s*u1[0]} y1={oy+s*u1[1]} x2={mx} y2={my} stroke="#888" strokeWidth="1"/>
              <line x1={ox+s*u2[0]} y1={oy+s*u2[1]} x2={mx} y2={my} stroke="#888" strokeWidth="1"/>
            </g>
          );
        })()}
        <text x={ox+22} y={oy-28} fontSize="10" fill="#888">90°</text>

        {/* Formula box */}
        <rect x={pL+10} y={H-88} width={PW-20} height={74} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={pL+18} y={H-72} fontSize="11" fill="#1F3864" fontWeight="700">Gram-Schmidt process:</text>
        <text x={pL+18} y={H-56} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">u1 = a1;  q1 = u1/||u1||</text>
        <text x={pL+18} y={H-40} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">u2 = a2 - (a2·q1)q1;  q2=u2/||u2||</text>
        <text x={pL+18} y={H-24} fontSize="10" fill="#555">qᵢ·qⱼ = delta_ij  (orthonormal columns)</text>

        {/* Right panel */}
        <rect x={pR} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pR+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">QR: Structure and Applications</text>

        {/* QR = A schematic */}
        <rect x={pR+10} y={36} width={PW-20} height={44} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={pR+PW/2} y={54} textAnchor="middle" fontSize="13" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">A = Q R</text>
        <text x={pR+PW/2} y={71} textAnchor="middle" fontSize="11" fill="#555">Q orthogonal (Q^T Q = I),  R upper triangular</text>

        {/* What Q and R encode */}
        {[
          { y:88, bg:'#f0fff4',bc:'#27ae60',color:'#27ae60',
            t:'Q (m x n orthonormal)', n:'Columns form orthonormal basis\nQ^T = Q^-1  (for square Q)' },
          { y:148, bg:'#fff7ee',bc:'#e07b00',color:'#e07b00',
            t:'R (n x n upper triangular)', n:'Encodes lengths and correlations\nDiagonal |rᵢᵢ| = length of projected aᵢ' },
        ].map(({ y, bg, bc, color, t, n }) => (
          <g key={y}>
            <rect x={pR+10} y={y} width={PW-20} height={52} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={pR+18} y={y+16} fontSize="12" fill={color} fontWeight="700">{t}</text>
            {n.split('\n').map((ln,j)=>(
              <text key={j} x={pR+18} y={y+32+j*14} fontSize="11" fill="#444">{ln}</text>
            ))}
          </g>
        ))}

        {/* Applications */}
        {[
          { y:208, bg:'#EBF3FB',bc:'#2E75B6',color:'#2E75B6',
            name:'Least squares', note:'min ||Ax-b||: solve Rx = Q^T b' },
          { y:254, bg:'#f5d0ff',bc:'#9b59b6',color:'#9b59b6',
            name:'Eigenvalue (QR alg)', note:'Iterative QR -> upper triangular' },
          { y:300, bg:'#fff0f0',bc:'#c0392b',color:'#c0392b',
            name:'Numerical stability', note:'QR via Householder/Givens more\nstable than classical Gram-Schmidt' },
        ].map(({ y, bg, bc, color, name, note }) => (
          <g key={y}>
            <rect x={pR+10} y={y} width={PW-20} height={40} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={pR+18} y={y+14} fontSize="11" fill={color} fontWeight="700">{name}</text>
            <text x={pR+18} y={y+29} fontSize="10" fill="#444">{note}</text>
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>QR factorisation writes A = QR where Q has orthonormal columns and R is upper triangular. Gram-Schmidt builds Q by repeatedly subtracting projections; Householder reflectors and Givens rotations are numerically superior alternatives. The QR factorisation is the foundation of the QR algorithm for eigenvalues and the standard method for least-squares problems.</div>
    </div>
  );
}

// ── Diagram 4: Condition Number & Stability ──────────────────────
function DiagramCondition() {
  const W = 900, H = 360;
  const PW = (W - 42) / 2;
  const pL = 14, pR = pL + PW + 14;

  // Left: geometric picture of condition number
  // Unit sphere mapped by A into an ellipse; ratio of semi-axes = kappa
  const cx = pL + PW/2, cy = 170, rx = 55, ry = 18;

  // Right: table of condition number values and consequences
  const rows = [
    { kappa:'1',        meaning:'Perfect (orthogonal matrix)', color:'#27ae60' },
    { kappa:'10',       meaning:'1 digit lost in double prec', color:'#27ae60' },
    { kappa:'1e6',      meaning:'~6 digits lost',             color:'#e07b00' },
    { kappa:'1e12',     meaning:'~12 digits lost (risky)',    color:'#c0392b' },
    { kappa:'1/eps~1e16', meaning:'Singular to working prec', color:'#c0392b' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Condition Number — Sensitivity, Stability, and the Geometry of Ill-Conditioning</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={pL} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pL+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Geometric View of Condition Number</text>
        <text x={pL+PW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">kappa(A) = sigma_max / sigma_min  (ratio of largest to smallest singular value)</text>

        {/* Unit circle */}
        <circle cx={cx-130} cy={cy} r={50} fill="#EBF3FB" fillOpacity="0.5" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={cx-130} y={cy+64} textAnchor="middle" fontSize="11" fill="#2E75B6">unit ball</text>
        <text x={cx-130} y={cy+78} textAnchor="middle" fontSize="10" fill="#555">||x||=1</text>

        {/* Arrow A-> */}
        <line x1={cx-74} y1={cy} x2={cx-24} y2={cy} stroke="#888" strokeWidth="1.5"/>
        <polygon points={`${cx-24},${cy} ${cx-32},${cy-4} ${cx-32},${cy+4}`} fill="#888"/>
        <text x={cx-49} y={cy-8} textAnchor="middle" fontSize="12" fill="#888" fontWeight="700">A</text>

        {/* Image ellipse */}
        <ellipse cx={cx+50} cy={cy} rx={rx} ry={ry} fill="#fff7ee" fillOpacity="0.6" stroke="#e07b00" strokeWidth="2"/>
        <text x={cx+50} y={cy+ry+14} textAnchor="middle" fontSize="11" fill="#e07b00">image A·(unit ball)</text>

        {/* Semi-axis labels */}
        <line x1={cx+50} y1={cy} x2={cx+50+rx} y2={cy} stroke="#c0392b" strokeWidth="2"/>
        <text x={cx+50+rx/2} y={cy-8} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">sigma_max</text>
        <line x1={cx+50} y1={cy} x2={cx+50} y2={cy-ry} stroke="#27ae60" strokeWidth="2"/>
        <text x={cx+50+6} y={cy-ry/2} fontSize="11" fill="#27ae60" fontWeight="700">sigma_min</text>

        {/* kappa label */}
        <rect x={pL+20} y={cy+40} width={PW-40} height={28} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={pL+PW/2} y={cy+54} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700" fontFamily="'Courier New',monospace">kappa(A) = sigma_max / sigma_min</text>
        <text x={pL+PW/2} y={cy+62} textAnchor="middle" fontSize="9" fill="#555">= ||A|| * ||A^-1||  (p=2 norm)</text>

        {/* Perturbation bound */}
        <rect x={pL+12} y={cy+76} width={PW-24} height={54} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={pL+20} y={cy+92} fontSize="11" fill="#c0392b" fontWeight="700">Perturbation bound (Ax=b):</text>
        <text x={pL+20} y={cy+108} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">||dx||/||x|| &lt;= kappa(A) * ||db||/||b||</text>
        <text x={pL+20} y={cy+122} fontSize="10" fill="#555">Relative error in x up to kappa(A) * relative error in b</text>

        {/* Well vs ill conditioned */}
        <rect x={pL+12} y={cy+138} width={PW-24} height={36} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={pL+20} y={cy+152} fontSize="11" fill="#27ae60" fontWeight="700">Well conditioned: kappa near 1  (nearly orthogonal)</text>
        <text x={pL+20} y={cy+167} fontSize="10" fill="#555">Ill conditioned: kappa large  (nearly singular, flat ellipse)</text>

        {/* Right panel */}
        <rect x={pR} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={pR+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Condition Number Reference</text>

        {/* Header */}
        <rect x={pR+10} y={36} width={PW-20} height={22} rx={2} fill="#2E75B6"/>
        <text x={pR+20}         y={51} fontSize="11" fill="#fff" fontWeight="700">kappa(A)</text>
        <text x={pR+PW/2+20}    y={51} fontSize="11" fill="#fff" fontWeight="700">Practical meaning</text>

        {rows.map(({ kappa, meaning, color }, i) => {
          const y = 58 + i * 38;
          return (
            <g key={i}>
              <rect x={pR+10} y={y} width={PW-20} height={34} rx={2}
                fill={i%2===0?'#f8fbff':'#fff'} stroke="#ddd" strokeWidth="0.8"/>
              <text x={pR+20} y={y+13} fontSize="11" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{kappa}</text>
              <text x={pR+20} y={y+27} fontSize="10" fill="#444">{meaning}</text>
            </g>
          );
        })}

        {/* Digits-lost formula */}
        <rect x={pR+10} y={58+5*38} width={PW-20} height={42} rx={3} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
        <text x={pR+18} y={58+5*38+16} fontSize="11" fill="#e07b00" fontWeight="700">Digits lost estimate:</text>
        <text x={pR+18} y={58+5*38+32} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">digits_lost ≈ log10(kappa(A))</text>

        {/* Forward/backward stability */}
        <rect x={pR+10} y={58+5*38+50} width={PW-20} height={60} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={pR+18} y={58+5*38+66} fontSize="11" fill="#1F3864" fontWeight="700">Backward stability</text>
        <text x={pR+18} y={58+5*38+82} fontSize="10" fill="#444">Alg is backward stable if fl(A)x = (A+dA)x</text>
        <text x={pR+18} y={58+5*38+96} fontSize="10" fill="#444">with ||dA|| &lt;= C*eps*||A||.  LU+pivot is b-stable.</text>

      </svg>
      <div style={S.dCaption}>The condition number kappa(A) = sigma_max / sigma_min measures how much a matrix stretches the unit ball into an ellipse. It bounds the worst-case relative error amplification: a solution may lose log10(kappa) decimal digits. Algorithms are backward stable if they solve a nearby problem exactly — stability and conditioning together determine final accuracy.</div>
    </div>
  );
}

// ── Diagram 5: Iterative Solvers ─────────────────────────────────
function DiagramIterative() {
  const W = 900, H = 370;
  const LP = 14, LW = 400;
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Left: convergence curves for Jacobi, Gauss-Seidel, CG (simulated)
  // Error vs iteration number
  const nIter = 20;
  function jacobiErr(k)    { return Math.pow(0.85, k); }
  function gsErr(k)        { return Math.pow(0.70, k); }
  function cgErr(k)        { return Math.pow(0.40, k); }

  const plotX = LP + 60, plotY = 280, plotW = LW - 80, plotH = 200;
  function px(k) { return plotX + (k / nIter) * plotW; }
  function py(v) { return plotY - Math.max(0, (-Math.log10(Math.max(v, 1e-10))) / 10 * plotH); }

  // Build polyline points
  const pts = n => Array.from({length:n+1},(_,k)=>k);
  const jacPts = pts(nIter).map(k=>`${px(k)},${py(jacobiErr(k))}`).join(' ');
  const gsPts  = pts(nIter).map(k=>`${px(k)},${py(gsErr(k))}`).join(' ');
  const cgPts  = pts(nIter).map(k=>`${px(k)},${py(cgErr(k))}`).join(' ');

  // Y axis labels: powers of 10
  const yTicks = [0, 2, 4, 6, 8, 10];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Iterative Solvers — Jacobi, Gauss-Seidel, and Conjugate Gradient Convergence</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Convergence Comparison (log-scale error)</text>
        <text x={LP+LW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">Ax = b with diagonally-dominant A (n = 50, illustrative rates)</text>

        {/* Y axis */}
        <line x1={plotX} y1={plotY} x2={plotX} y2={plotY-plotH} stroke="#888" strokeWidth="1.2"/>
        {yTicks.map(t => {
          const yy = plotY - (t/10)*plotH;
          return (
            <g key={t}>
              <line x1={plotX-4} y1={yy} x2={plotX+plotW} y2={yy} stroke="#eee" strokeWidth="1"/>
              <text x={plotX-8} y={yy+4} textAnchor="end" fontSize="10" fill="#aaa">1e-{t||'0'}</text>
            </g>
          );
        })}
        <text x={LP+16} y={plotY-plotH/2} fontSize="10" fill="#888"
          transform={`rotate(-90,${LP+16},${plotY-plotH/2})`}>Error ||r||</text>

        {/* X axis */}
        <line x1={plotX} y1={plotY} x2={plotX+plotW} y2={plotY} stroke="#888" strokeWidth="1.2"/>
        {[0,5,10,15,20].map(k => (
          <g key={k}>
            <line x1={px(k)} y1={plotY} x2={px(k)} y2={plotY+4} stroke="#aaa" strokeWidth="1"/>
            <text x={px(k)} y={plotY+14} textAnchor="middle" fontSize="10" fill="#aaa">{k}</text>
          </g>
        ))}
        <text x={plotX+plotW/2} y={plotY+26} textAnchor="middle" fontSize="11" fill="#888">Iteration k</text>

        {/* Curves */}
        <polyline points={jacPts} fill="none" stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>
        <polyline points={gsPts}  fill="none" stroke="#2E75B6" strokeWidth="2" strokeDasharray="4,2"/>
        <polyline points={cgPts}  fill="none" stroke="#27ae60" strokeWidth="2.5"/>

        {/* Legend */}
        {[
          { color:'#e07b00', dash:'6,3', sw:2,   label:'Jacobi       (rho~0.85)' },
          { color:'#2E75B6', dash:'4,2', sw:2,   label:'Gauss-Seidel (rho~0.70)' },
          { color:'#27ae60', dash:'',    sw:2.5, label:'Conj. Gradient (rho~0.40)' },
        ].map(({ color, dash, sw, label }, i) => (
          <g key={i}>
            <line x1={plotX+8} y1={plotY-plotH-30+i*18} x2={plotX+36} y2={plotY-plotH-30+i*18}
              stroke={color} strokeWidth={sw} strokeDasharray={dash}/>
            <text x={plotX+42} y={plotY-plotH-26+i*18} fontSize="11" fill={color} fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Right panel */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">Iterative Method Summaries</text>

        {[
          { y:36, bg:'#fff7ee', bc:'#e07b00', color:'#e07b00',
            name:'Jacobi',
            formula:'x_i^(k+1) = (b_i - sum_{j!=i} a_ij x_j^k) / a_ii',
            note:'Uses only old iterates; easily parallelised\nConverges if A strictly diag-dominant' },
          { y:108, bg:'#EBF3FB', bc:'#2E75B6', color:'#2E75B6',
            name:'Gauss-Seidel',
            formula:'x_i^(k+1) uses newest available x_j',
            note:'Uses updated values immediately\n~2x faster than Jacobi per iteration' },
          { y:180, bg:'#f0fff4', bc:'#27ae60', color:'#27ae60',
            name:'Conjugate Gradient (CG)',
            formula:'p_{k+1} = r_{k+1} + beta_k p_k',
            note:'Optimal for SPD matrices; O(n*k) total\nConverges in <=n steps (exact arithmetic)' },
          { y:252, bg:'#f5d0ff', bc:'#9b59b6', color:'#9b59b6',
            name:'Preconditioned CG (PCG)',
            formula:'Solve M^-1 A x = M^-1 b',
            note:'M approx A reduces kappa\nCommon: ILU, SSOR, multigrid precond.' },
          { y:318, bg:'#e8eaf6', bc:'#1F3864', color:'#1F3864',
            name:'GMRES',
            formula:'Minimise ||b - Ax|| over Krylov subspace',
            note:'General non-symmetric A\nRestart controls memory: GMRES(m)' },
        ].map(({ y, bg, bc, color, name, formula, note }) => (
          <g key={y}>
            <rect x={RP+10} y={y} width={RW-20} height={64} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+18} y={y+15} fontSize="12" fill={color} fontWeight="700">{name}</text>
            <text x={RP+18} y={y+30} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
            {note.split('\n').map((ln,j)=>(
              <text key={j} x={RP+18} y={y+46+j*13} fontSize="10" fill="#555">{ln}</text>
            ))}
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>Iterative methods build a sequence of approximations without factorising A — essential when n is large or A is sparse. Jacobi and Gauss-Seidel are classical stationary methods; the Conjugate Gradient method is the gold standard for symmetric positive-definite systems, converging in at most n steps. Preconditioning reduces the effective condition number and dramatically accelerates convergence.</div>
    </div>
  );
}

// ── Diagram 6: SVD — Singular Value Decomposition ────────────────
function DiagramSVD() {
  const W = 900, H = 360;
  const LP = 14, LW = 460;
  const RP = LP + LW + 18, RW = W - RP - 14;

  // Left: geometric view — unit circle -> ellipse via U S V^T
  // and rank-1 outer product decomposition
  const cx1 = LP + 120, cy1 = 160, r0 = 60;
  // Singular values sigma1=2.5, sigma2=0.8 (illustrative)
  const s1 = 2.2, s2 = 0.65;
  // Rotation angle for U
  const theta = 30 * Math.PI / 180;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Singular Value Decomposition (SVD) — Geometry, Rank Approximation, and Applications</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* Left panel */}
        <rect x={LP} y={10} width={LW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={LP+LW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">SVD Geometry: A = U Sigma V^T</text>
        <text x={LP+LW/2} y={44} textAnchor="middle" fontSize="11" fill="#555">Three steps: rotate (V^T), scale (Sigma), rotate (U)</text>

        {/* Step 1: unit circle */}
        <circle cx={cx1} cy={cy1} r={r0} fill="#EBF3FB" fillOpacity="0.4" stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={cx1} y={cy1+r0+14} textAnchor="middle" fontSize="10" fill="#2E75B6">unit ball</text>
        <text x={cx1} y={cy1+r0+26} textAnchor="middle" fontSize="9" fill="#555">||x||=1</text>

        {/* Arrow V^T */}
        <line x1={cx1+r0+8} y1={cy1} x2={cx1+r0+36} y2={cy1} stroke="#888" strokeWidth="1.5"/>
        <polygon points={`${cx1+r0+36},${cy1} ${cx1+r0+28},${cy1-4} ${cx1+r0+28},${cy1+4}`} fill="#888"/>
        <text x={cx1+r0+22} y={cy1-8} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">V^T</text>
        <text x={cx1+r0+22} y={cy1+18} textAnchor="middle" fontSize="9" fill="#888">rotate</text>

        {/* Step 2: axis-aligned ellipse after V^T then Sigma */}
        <ellipse cx={cx1+r0+90} cy={cy1} rx={r0*s1} ry={r0*s2}
          fill="#fff7ee" fillOpacity="0.5" stroke="#e07b00" strokeWidth="1.8"/>
        {/* Sigma axes */}
        <line x1={cx1+r0+90-r0*s1} y1={cy1} x2={cx1+r0+90+r0*s1} y2={cy1}
          stroke="#e07b00" strokeWidth="1.5" strokeDasharray="3,2"/>
        <line x1={cx1+r0+90} y1={cy1-r0*s2} x2={cx1+r0+90} y2={cy1+r0*s2}
          stroke="#e07b00" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={cx1+r0+90+r0*s1+4} y={cy1+4} fontSize="10" fill="#e07b00" fontWeight="700">sigma1</text>
        <text x={cx1+r0+90+4} y={cy1-r0*s2-4} fontSize="10" fill="#e07b00" fontWeight="700">sigma2</text>

        {/* Arrow U */}
        <line x1={cx1+r0+90+r0*s1+8} y1={cy1} x2={cx1+r0+90+r0*s1+36} y2={cy1} stroke="#888" strokeWidth="1.5"/>
        <polygon points={`${cx1+r0+90+r0*s1+36},${cy1} ${cx1+r0+90+r0*s1+28},${cy1-4} ${cx1+r0+90+r0*s1+28},${cy1+4}`} fill="#888"/>
        <text x={cx1+r0+90+r0*s1+22} y={cy1-8} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">U</text>
        <text x={cx1+r0+90+r0*s1+22} y={cy1+18} textAnchor="middle" fontSize="9" fill="#888">rotate</text>

        {/* Step 3: rotated ellipse */}
        {(() => {
          const ecx = cx1+r0+90+r0*s1+90;
          const ecy = cy1;
          return (
            <g transform={`rotate(${theta*180/Math.PI},${ecx},${ecy})`}>
              <ellipse cx={ecx} cy={ecy} rx={r0*s1} ry={r0*s2}
                fill="#f5d0ff" fillOpacity="0.5" stroke="#9b59b6" strokeWidth="2"/>
              <text x={ecx} y={ecy+r0*s2+14} textAnchor="middle" fontSize="10" fill="#9b59b6">A*(unit ball)</text>
            </g>
          );
        })()}

        {/* Rank-1 sum visual */}
        <text x={LP+LW/2} y={cy1+r0+52} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          A = sigma1 u1 v1^T + sigma2 u2 v2^T + ...  (sum of rank-1 matrices)
        </text>

        {/* Truncated SVD illustration */}
        <rect x={LP+12} y={cy1+r0+66} width={LW-24} height={44} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={LP+20} y={cy1+r0+82} fontSize="11" fill="#27ae60" fontWeight="700">Best rank-k approximation (Eckart-Young theorem):</text>
        <text x={LP+20} y={cy1+r0+98} fontSize="11" fill="#333" fontFamily="'Courier New',monospace">A_k = sigma1 u1 v1^T + ... + sigma_k u_k v_k^T</text>

        {/* Sigma values bar chart */}
        <text x={LP+20} y={cy1+r0+124} fontSize="11" fill="#1F3864" fontWeight="700">Singular value spectrum (illustrative):</text>
        {[1.0, 0.7, 0.35, 0.15, 0.05, 0.02].map((sv, i) => {
          const bx = LP + 20 + i * 58, by = cy1+r0+140, bh = sv * 60;
          const colors = ['#2E75B6','#27ae60','#e07b00','#9b59b6','#c0392b','#888'];
          return (
            <g key={i}>
              <rect x={bx} y={by-bh} width={44} height={bh} rx={2} fill={colors[i]} fillOpacity="0.7"/>
              <text x={bx+22} y={by+12} textAnchor="middle" fontSize="9" fill="#888">s{i+1}</text>
              <text x={bx+22} y={by-bh-4} textAnchor="middle" fontSize="9" fill={colors[i]}>{sv}</text>
            </g>
          );
        })}

        {/* Right panel */}
        <rect x={RP} y={10} width={RW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={RP+RW/2} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">SVD Applications</text>

        {[
          { y:36, bg:'#EBF3FB', bc:'#2E75B6', color:'#2E75B6',
            name:'Pseudoinverse A+',
            formula:'A+ = V Sigma+ U^T',
            note:'Sigma+: invert nonzero diag entries\nMinimum-norm least-squares solution x=A+b' },
          { y:110, bg:'#f0fff4', bc:'#27ae60', color:'#27ae60',
            name:'Rank and Null Space',
            formula:'rank(A) = # nonzero singular values',
            note:'null(A) = columns of V for sigma=0\nrange(A) = columns of U for sigma!=0' },
          { y:184, bg:'#fff7ee', bc:'#e07b00', color:'#e07b00',
            name:'PCA (data compression)',
            formula:'A_k keeps largest k sing. values',
            note:'Captures max variance in k dimensions\nError ||A - A_k||_F = sqrt(sum sigma_j^2, j>k)' },
          { y:258, bg:'#f5d0ff', bc:'#9b59b6', color:'#9b59b6',
            name:'Condition Number',
            formula:'kappa(A) = sigma_max / sigma_min',
            note:'SVD gives exact condition number\nSingular if any sigma = 0' },
        ].map(({ y, bg, bc, color, name, formula, note }) => (
          <g key={y}>
            <rect x={RP+10} y={y} width={RW-20} height={66} rx={3} fill={bg} stroke={bc} strokeWidth="1"/>
            <text x={RP+18} y={y+16} fontSize="12" fill={color} fontWeight="700">{name}</text>
            <text x={RP+18} y={y+31} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{formula}</text>
            {note.split('\n').map((ln,j)=>(
              <text key={j} x={RP+18} y={y+47+j*14} fontSize="10" fill="#555">{ln}</text>
            ))}
          </g>
        ))}

      </svg>
      <div style={S.dCaption}>Every matrix A = U Sigma V^T decomposes into two orthogonal transformations (rotations/reflections) sandwiching a diagonal scaling. The singular values sigma_i are the semi-axes of the image ellipse. The truncated SVD A_k is the best rank-k approximation (Eckart-Young), fundamental to PCA, image compression, and the pseudoinverse.</div>
    </div>
  );
}

// ── Diagram 7: Eigenvalue Algorithms ────────────────────────────
function DiagramEigenvalue() {
  const W = 900, H = 330;
  const ncols = 3;
  const PW = (W - 14 - (ncols-1)*14 - 14) / ncols;
  const p = i => 14 + i*(PW+14);

  // Panel 1: Power iteration convergence
  // Panel 2: QR algorithm schematic (Hessenberg -> triangular)
  // Panel 3: Method comparison table

  const methods = [
    { name:'Power Iteration', color:'#2E75B6', bg:'#EBF3FB', bc:'#2E75B6',
      formula:'x_{k+1} = A x_k / ||A x_k||',
      conv:'Converges to dominant eigvec\nRate: |lambda2/lambda1|^k',
      use:'Largest eigenvalue\nPageRank, Markov chains',
      limit:'Only one eigenpair\nSlow if eigenvalues close' },
    { name:'QR Algorithm', color:'#27ae60', bg:'#f0fff4', bc:'#27ae60',
      formula:'A0=A; Ak=Qk Rk; A_{k+1}=Rk Qk',
      conv:'Converges to Schur form (triangular)\nShifts accelerate convergence',
      use:'All eigenvalues of general A\nIndustry standard (LAPACK)',
      limit:'O(n^3) per iteration\nFirst reduce to Hessenberg: O(n^3)' },
    { name:'Lanczos / Arnoldi', color:'#9b59b6', bg:'#f5d0ff', bc:'#9b59b6',
      formula:'Build orthonormal Krylov basis:\n{b, Ab, A^2b, ...}',
      conv:'Finds k eigenpairs in O(k*n*nnz)\nFast for sparse large A',
      use:'Largest/smallest eigenpairs\nSparse PDE discretisations',
      limit:'Finite precision causes\nghost eigenvalues (reorthog. needed)' },
  ];

  const rowH = 44;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Eigenvalue Algorithms — Power Iteration, QR Algorithm, and Krylov Methods</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {methods.map(({ name, color, bg, bc, formula, conv, use, limit }, i) => {
          const cx = p(i) + PW/2;
          const sections = [
            { label:'Update formula', text: formula, color:'#1F3864' },
            { label:'Convergence',    text: conv,    color:'#555' },
            { label:'Best for',       text: use,     color:'#333' },
            { label:'Limitation',     text: limit,   color:'#c0392b' },
          ];
          const fLines = formula.split('\n').length;
          const yOff = 50 + fLines * 16 + 18;
          return (
            <g key={i}>
              <rect x={p(i)} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              {/* Header */}
              <rect x={p(i)} y={10} width={PW} height={32} rx={5} fill={color}/>
              <rect x={p(i)} y={32} width={PW} height={10} fill={color}/>
              <text x={cx} y={30} textAnchor="middle" fontSize="13" fill="#fff" fontWeight="700">{name}</text>

              {/* Formula highlight */}
              <rect x={p(i)+10} y={50} width={PW-20} height={fLines*16+10} rx={3} fill={bg} stroke={bc} strokeWidth="1.2"/>
              {formula.split('\n').map((ln,j)=>(
                <text key={j} x={p(i)+18} y={64+j*16} fontSize="10.5" fill={color}
                  fontFamily="'Courier New',monospace" fontWeight="700">{ln}</text>
              ))}

              {sections.slice(1).map(({ label, text, color:tc }, j) => {
                const secY = yOff + j * (text.split('\n').length * 15 + 28);
                return (
                  <g key={j}>
                    <text x={p(i)+14} y={secY + 14} fontSize="10" fill="#888" fontWeight="700">{label}:</text>
                    {text.split('\n').map((ln, k) => (
                      <text key={k} x={p(i)+14} y={secY+28+k*14} fontSize="10.5" fill={tc}>{ln}</text>
                    ))}
                  </g>
                );
              })}
            </g>
          );
        })}

      </svg>
      <div style={S.dCaption}>Power iteration is the simplest eigenvalue algorithm but finds only the dominant eigenpair. The QR algorithm — after a preliminary reduction to Hessenberg form — iteratively produces the Schur factorisation containing all eigenvalues; it is the core of LAPACK's DGEEV. Lanczos and Arnoldi build a low-dimensional Krylov basis to extract a few eigenpairs from large sparse matrices without ever forming A explicitly.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function NumericalLinearAlgebra() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Numerical Linear Algebra</h1>
      <p style={S.p}>Numerical linear algebra is the study of algorithms for matrix computations — solving linear systems, finding eigenvalues, and decomposing matrices — with attention to the finite-precision arithmetic of computers. Every computation introduces rounding errors, and the central questions are: how large can those errors become, and can they be controlled? The answers depend on the condition number of the problem and the stability of the algorithm. Well-designed algorithms (LU with pivoting, QR via Householder, the QR eigenvalue algorithm) are backward stable: they solve a slightly perturbed problem exactly, so the only error amplification comes from the inherent sensitivity of the problem itself.</p>

      <InfoBox title="Core Idea: Accuracy = Stability of algorithm + Conditioning of problem" items={[
        "Floating-point arithmetic is inexact: every real number is rounded to the nearest representable float",
        "Machine epsilon eps = 2^-52 ≈ 2.22e-16 (double): smallest relative gap between consecutive floats near 1",
        "Condition number kappa(A) = ||A|| ||A^-1||: amplification factor for errors; log10(kappa) ≈ digits lost",
        "Backward stability: algorithm computes exact answer for (A + delta_A); final error bounded by kappa * eps",
        "LU with partial pivoting: the standard method for dense Ax=b; O(2n^3/3) flops",
        "SVD: A = U Sigma V^T — the most informative decomposition; gives rank, condition, pseudoinverse, best low-rank approx",
      ]}/>

      <h2 style={S.h2}>Floating-Point Arithmetic and Error Sources</h2>
      <DiagramFloatingPoint/>
      <Table2 rows={[
        ["IEEE 754 double",       "64 bits: 1 sign + 11 exponent + 52 mantissa\nRange ~5e-324 to ~1.8e+308, ~15-17 significant decimal digits"],
        ["Machine epsilon",       "eps = 2^-52 ≈ 2.22e-16  (unit roundoff u = eps/2)\n|fl(x) - x| / |x| <= u for all representable x"],
        ["Rounding error",        "Each arithmetic op introduces relative error <= u\nAfter n ops: accumulated error O(n*u) in stable algorithms"],
        ["Catastrophic cancellation","a - b when a ≈ b: relative error in result can be huge\nAll significant digits may cancel; reformulate to avoid"],
        ["Overflow / underflow",  "Overflow -> Inf; underflow -> 0 or subnormal\nSubnormals extend range at cost of precision"],
        ["Kahan summation",       "Compensated summation reduces rounding to O(u)\nTracks and adds back lost low-order bits each step"],
      ]}/>

      <h2 style={S.h2}>LU Factorisation and Gaussian Elimination</h2>
      <DiagramLU/>
      <Table2 rows={[
        ["LU factorisation",      "A = L U,  L unit lower triangular, U upper triangular\nGaussian elimination stores multipliers in L"],
        ["Partial pivoting",      "PA = LU: permute rows so |pivot| is largest in column\nLimits growth in L; essential for stability in practice"],
        ["Complete pivoting",     "PAQ = LU: permute rows and columns\nStronger stability guarantee; rarely needed"],
        ["Forward substitution",  "Ly = Pb: O(n^2) — works top to bottom\nUnit diagonal of L means no division needed"],
        ["Back substitution",     "Ux = y: O(n^2) — works bottom to top\nRequires U diagonal to be nonzero"],
        ["Multiple right-hand sides","Factorise A once O(2n^3/3); solve each b in O(n^2)\nCritical for iterative refinement and matrix inversion"],
        ["Cholesky (SPD only)",   "A = L L^T: half the work of LU, always stable\nRequires A symmetric positive-definite"],
      ]}/>

      <h2 style={S.h2}>QR Factorisation</h2>
      <DiagramQR/>
      <Table2 rows={[
        ["QR factorisation",      "A = QR,  Q orthogonal (Q^T Q = I),  R upper triangular\nAlways exists; unique if A full rank and R diag > 0"],
        ["Gram-Schmidt (classical)","Orthogonalise columns of A iteratively\nNumerically unstable; use modified Gram-Schmidt instead"],
        ["Householder reflectors","H = I - 2vv^T / (v^T v): orthogonal reflection\nZeroes out subdiagonal entries; numerically stable O(2mn^2 - 2n^3/3)"],
        ["Givens rotations",      "G: 2x2 rotation eliminating one entry at a time\nUseful for sparse A or updating existing QR"],
        ["Least squares via QR",  "min ||Ax - b||_2: solve Rx = Q^T b  (upper triangular)\nBackward stable; preferred over normal equations A^T A x = A^T b"],
        ["Thin vs full QR",       "Thin (economy) QR: Q is m x n (only first n columns)\nFull QR: Q is m x m; full null space included"],
      ]}/>

      <h2 style={S.h2}>Condition Number and Numerical Stability</h2>
      <DiagramCondition/>
      <Table2 rows={[
        ["Condition number",      "kappa_p(A) = ||A||_p ||A^-1||_p\nkappa_2(A) = sigma_max / sigma_min (ratio of singular values)"],
        ["Perturbation bound",    "||delta_x|| / ||x|| <= kappa(A) * (||delta_b|| / ||b|| + ||delta_A|| / ||A||)\nCondition number is the worst-case error amplification factor"],
        ["Digits lost",           "A solve loses approximately log10(kappa(A)) decimal digits\nDouble has ~16 digits; kappa=1e10 leaves only ~6 correct digits"],
        ["Backward stability",    "Algorithm is backward stable if fl_alg(A) = exact_alg(A + dA)\nwith ||dA|| = O(eps) ||A||; then forward error <= kappa * O(eps)"],
        ["Forward stability",     "Algorithm is forward stable if output error is O(kappa * eps)\nNot all backward stable algorithms are forward stable"],
        ["Iterative refinement",  "Solve, compute residual r = b - Ax, solve Ad = r, x += d\nCan recover full precision even from ill-conditioned systems"],
      ]}/>

      <h2 style={S.h2}>Iterative Solvers for Large Systems</h2>
      <DiagramIterative/>
      <Table2 rows={[
        ["When to use iterative","Direct methods (LU): dense A, small-medium n\nIterative: large sparse A where LU fill-in is prohibitive"],
        ["Splitting methods",     "A = M - N; x^(k+1) = M^-1(N x^k + b)\nJacobi: M=D; Gauss-Seidel: M=L+D; converge if spectral radius rho(M^-1 N) < 1"],
        ["Krylov subspace",       "Build K_k = span{b, Ab, A^2 b,...,A^(k-1)b}\nCG, GMRES, BiCGSTAB all search in K_k"],
        ["Conjugate Gradient",    "For SPD A: minimises ||x-x*||_A over K_k\nConverges in n steps; rate depends on sqrt(kappa)"],
        ["GMRES",                 "General A: minimises ||b-Ax||_2 over K_k\nFull GMRES is too expensive; restart with GMRES(m)"],
        ["Preconditioning",       "Solve M^-1 Ax = M^-1 b with M approx A\nGoal: kappa(M^-1 A) << kappa(A); common: ILU, AMG, SSOR"],
        ["Convergence criterion", "Stop when ||b - Ax|| / ||b|| < tolerance (e.g. 1e-10)\nRelative residual is the standard stopping check"],
      ]}/>

      <h2 style={S.h2}>Singular Value Decomposition</h2>
      <DiagramSVD/>
      <Table2 rows={[
        ["SVD definition",        "A = U Sigma V^T  (A: m x n)\nU: m x m orthogonal; Sigma: m x n diag (sigma_i >= 0); V: n x n orthogonal"],
        ["Singular values",       "sigma_1 >= sigma_2 >= ... >= sigma_min(m,n) >= 0\nSquare roots of eigenvalues of A^T A  (or A A^T)"],
        ["Eckart-Young theorem",  "||A - A_k||_2 = sigma_{k+1}  (min over all rank-k matrices)\nTruncated SVD is the best low-rank approximation in 2- and F-norms"],
        ["Pseudoinverse A+",      "A+ = V Sigma+ U^T  (invert nonzero sigma, zero the rest)\nGives minimum-norm least-squares solution to Ax=b"],
        ["Numerical rank",        "rank(A) := # sigma_i > tolerance * sigma_1\nUsed when exact rank determination is ill-posed"],
        ["Computing the SVD",     "Golub-Reinsch: bidiagonalise then QR iteration  O(mn^2) for m>=n\nRandomised SVD: O(mn log k) for rank-k approximation"],
      ]}/>

      <h2 style={S.h2}>Eigenvalue Algorithms</h2>
      <DiagramEigenvalue/>
      <Table2 rows={[
        ["Power iteration",       "x_{k+1} = Ax_k / ||Ax_k||; converges to dominant eigenvector\nRate |lambda_2 / lambda_1|^k; fails if top two eigenvalues equal in magnitude"],
        ["Inverse iteration",     "Solve (A - mu I)x_{k+1} = x_k; converges to eigenvec near mu\nUsed to refine an eigenvalue estimate"],
        ["Rayleigh quotient iter","mu_k = x_k^T A x_k / x_k^T x_k; cubic convergence\nCombine with inverse iteration for fast refinement"],
        ["QR algorithm",          "Ak+1 = Rk Qk (where Ak = Qk Rk); converges to Schur form\nPractical: first reduce to Hessenberg O(10n^3/3), then QR with shifts"],
        ["Symmetric case",        "A symmetric -> eigenvalues real; use symmetric QR (tridiag first)\nLanczos method exploits symmetry for large sparse A"],
        ["Schur decomposition",   "A = Q T Q^T  (Q orthogonal, T upper triangular, diag = eigenvalues)\nBackward stable; complex eigenvalues appear as 2x2 blocks in real Schur form"],
      ]}/>

      <InfoBox title="Key Takeaway — Algorithm Selection Guide" items={[
        "Dense Ax=b, general A? -> LU with partial pivoting (LAPACK DGESV); O(2n^3/3)",
        "Dense Ax=b, A symmetric positive-definite? -> Cholesky (DPOTRF); half the ops, always stable",
        "Least-squares min ||Ax-b||? -> QR via Householder (DGELS); more stable than normal equations",
        "All eigenvalues of dense A? -> QR algorithm (DGEEV); O(25n^3) with Hessenberg reduction",
        "SVD of dense A? -> Golub-Reinsch (DGESVD); O(mn^2) for m>=n; use randomised SVD for low rank",
        "Large sparse Ax=b, SPD? -> Preconditioned Conjugate Gradient; O(k * nnz) for k iterations",
        "Large sparse Ax=b, general? -> GMRES with ILU preconditioner",
        "A few eigenpairs of large sparse A? -> Lanczos (symmetric) or Arnoldi (general) Krylov methods",
      ]}/>
    </div>
  );
}